import React, { useState } from 'react';
import { Container, Header, Segment, Icon, List, Divider, Image, Button, Modal } from 'semantic-ui-react';
import '@styles/BlogBody.css';
import '@styles/GeneralTheme.css';
import WebToLead from '@components/WebToLead'; // Import the WebToLead component

const About = () => {
  const [modalOpen, setModalOpen] = useState(false); // State to manage modal visibility
  const [isDesktop, setIsDesktop] = useState(false);

  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Add Open Graph meta tags for about page
  React.useEffect(() => {
    const updateMetaTag = (property, content, isProperty = true) => {
      const attribute = isProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(attribute, property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    document.title = 'About - Cloud CZR | Professional Salesforce Development & Technology Consulting';
    updateMetaTag('og:title', 'About - Cloud CZR | Professional Salesforce Development & Technology Consulting');
    updateMetaTag('og:description', 'Learn about Cloud CZR\'s mission to blend ancient wisdom with modern technology. We provide expert Salesforce development and technology consulting services.');
    updateMetaTag('og:url', window.location.href);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:image', `${window.location.origin}/assets/CloudChiRho.png`);
    updateMetaTag('og:image:alt', 'Cloud CZR, Cloudczr, cloudczr.com');
    
    updateMetaTag('description', 'Learn about Cloud CZR\'s mission to blend ancient wisdom with modern technology. We provide expert Salesforce development and technology consulting services.', false);
    updateMetaTag('twitter:title', 'About - Cloud CZR | Professional Salesforce Development & Technology Consulting', false);
    updateMetaTag('twitter:description', 'Learn about Cloud CZR\'s mission to blend ancient wisdom with modern technology. We provide expert Salesforce development and technology consulting services.', false);
  }, []);

  const getTextStyle = () => ({
    color: '#702963',
    fontSize: isDesktop ? '1.4em' : '1.2em',
    lineHeight: '1.6'
  });

  const getHeaderStyle = () => ({
    color: '#a9885c',
    fontSize: isDesktop ? '2.5em' : '2.0em',
    lineHeight: '1.4'
  });

  const getSubheaderStyle = () => ({
    color: '#a9885c',
    fontSize: isDesktop ? '1.1em' : '1.0em',
    lineHeight: '1.6'
  });

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Container fluid style={{ marginTop: '2em', padding: '0 2em', maxWidth: 'none !important', width: '100% !important' }} className="body-gold">
      <Header as='h1' textAlign='center' style={getHeaderStyle()}>
        <Image src={`${process.env.PUBLIC_URL}/assets/about.svg`} alt="Cloud CZR, Cloudczr, cloudczr.com" style={{ width: isDesktop ? '65%' : '100%', height: 'auto', margin: '0 auto 15px auto', display: 'block' }} />
      </Header>
    
      <Divider section />
      <Segment padded style={{ width: '100%', maxWidth: 'none' }}>
        <Header as='h2' icon='hand spock' content='Our Mission' style={{ color: '#a9885c' }}/>
        <div style={{ backgroundColor: 'white', padding: '1.5em', borderRadius: '8px', margin: '1em 0' }}>
          <List bulleted relaxed>
            <List.Item style={getTextStyle()}>
              <strong>Technology Hub:</strong> This website serves as a hub for insights into the evolving world of technology, with a special focus on Salesforce development and consulting.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Industry Adaptability:</strong> We adapt to any industry and any size of company. Whether you're a startup, a small business, or a large enterprise, we are here to help.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Diverse Partnerships:</strong> We would love to work with companies who share out values and want to create something great together. We are open to partnerships with companies of all sizes and industries.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Empowerment Focus:</strong> Built to empower businesses and individuals alike, this site provides a wealth of resources.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Expert Content:</strong> From expert articles on Salesforce and technology trends to hands-on tips for mindfulness and productivity.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Strategic Guidance:</strong> Here, you'll find the guidance you need to stay ahead in today's fast-paced digital landscape.
            </List.Item>
          </List>
        </div>
      </Segment>

      <Divider hidden />

      <Segment padded style={{ width: '100%', maxWidth: 'none' }}>
        <Header as='h2' style={{ color: '#a9885c' }}>
          <Icon name='map signs' />
          What You'll Find Here
        </Header>
        <List divided relaxed>
          <List.Item style={{ backgroundColor: 'white', padding: '1em', margin: '0.5em 0', borderRadius: '8px' }}>
            <Icon name='code' size='large' style={{ color: '#a9885c' }}/>
            <List.Content>
              <List.Header as='h3' style={{ color: '#a9885c' }}>Salesforce Expertise</List.Header>
              <List.Description style={getTextStyle()}>
                Comprehensive resources and articles on Salesforce development, integrations, and custom solutions. 
                From Apex to Lightning Components, we delve into the technical depth that supports your business goals.
              </List.Description>
            </List.Content>
          </List.Item>
          <List.Item style={{ backgroundColor: 'white', padding: '1em', margin: '0.5em 0', borderRadius: '8px' }}>
            <Icon name='handshake' size='large' style={{ color: '#a9885c' }} />
            <List.Content>
              <List.Header as='h3' style={{ color: '#a9885c' }}>Consulting Services</List.Header>
              <List.Description style={getTextStyle()}>
                Practical insights into leveraging technology for business growth. Whether you're in need of Salesforce consulting 
                or tech strategy advice, this site offers valuable guidance to optimize and scale your operations.
              </List.Description>
            </List.Content>
          </List.Item>
          <List.Item style={{ backgroundColor: 'white', padding: '1em', margin: '0.5em 0', borderRadius: '8px' }}>
            <Icon name='book' size='large' style={{ color: '#a9885c' }}/>
            <List.Content>
              <List.Header as='h3' style={{ color: '#a9885c' }}>Engaging Blog</List.Header>
              <List.Description style={getTextStyle()}>
                Articles on diverse topics, including technology, history, and mindfulness. Our blog covers everything from 
                JavaScript innovations to the significance of historical events and tips for achieving mental clarity.
              </List.Description>
            </List.Content>
          </List.Item>
        </List>
      </Segment>

      <Divider section />

      <Segment padded style={{ width: '100%', maxWidth: 'none' }}>
        <Header as='h2' icon='leaf' content='Our Purpose' style={{ color: '#a9885c' }}/>
        <div style={{ backgroundColor: 'white', padding: '1.5em', borderRadius: '8px', margin: '1em 0' }}>
          <List bulleted relaxed>
            <List.Item style={getTextStyle()}>
              <strong>Dual Mission:</strong> This site was created not only to offer consulting services but also to share a deeper perspective on how technology and mindfulness intersect.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Goal Achievement:</strong> Enabling you to achieve your business and personal goals more effectively.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Holistic Approach:</strong> We believe in a holistic approach to technology – one that values efficiency, strategy, and well-being.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Open Invitation:</strong> Whether you're here to explore solutions for Salesforce or just seeking inspiration for your own projects, we're glad to have you join us on this journey.
            </List.Item>
          </List>
        </div>
      </Segment>

      <Divider hidden />

      <Segment padded textAlign='center' style={{ width: '100%', maxWidth: 'none' }}>
        <Header as='h2' icon='users' content='Get to Know Us' style={{ color: '#a9885c' }}/>
        <div style={{ backgroundColor: 'white', padding: '1.5em', borderRadius: '8px', margin: '1em 0' }}>
          <List bulleted relaxed style={{ textAlign: 'left' }}>
            <List.Item style={getTextStyle()}>
              <strong>Browse Our Content:</strong> Browse the site to discover valuable resources and insights.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Explore Services:</strong> Explore our comprehensive range of professional services.
            </List.Item>
            <List.Item style={getTextStyle()}>
              <strong>Connect With Us:</strong> Reach out to discuss how we can help you thrive and achieve your goals.
            </List.Item>
          </List>
        </div>
        <Button primary size='large' className="custom-button-body" onClick={handleOpenModal}>Get In Touch</Button>
      </Segment>

      {/* Contact Form Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal} size='small' style={{ backgroundColor: '#702963' }}>
        <Modal.Header style={{ backgroundColor: '#702963', color: '#a9885c' }}>Contact Us</Modal.Header>
        <Modal.Content style={{ backgroundColor: '#a9885c' }}>
          <WebToLead />
        </Modal.Content>
        <Modal.Actions style={{ backgroundColor: '#702963', color: '#a9885c' }}>
          <Button style={{ backgroundColor: '#a9885c', color: '#702963' }} onClick={handleCloseModal}>Close</Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default About;
